import React, { useState, useEffect } from 'react'
import { Calendar, Music, Users, Clock, Trophy, ChevronRight, ChevronLeft, Sparkles, LayoutPanelLeft, Search, CheckCircle2, PlayCircle, Download } from 'lucide-react'
import { Button, Card, Input } from '../ui'
import { cn } from '../../lib/utils'
import toast from 'react-hot-toast'
import AnimatedGradientBackground from '../AnimatedGradientBackground'

const AdminWrappedTab = ({ rooms, loadingRooms, fetchRooms }) => {
    const [selectedRoomIds, setSelectedRoomIds] = useState([])
    const [isGenerating, setIsGenerating] = useState(false)
    const [wrappedData, setWrappedData] = useState(null)
    const [currentSlide, setCurrentSlide] = useState(0)
    const [roomSearch, setRoomSearch] = useState('')
    const [sortBy, setSortBy] = useState('activity') // 'activity' or 'songs'

    const toggleRoom = (id) => {
        setSelectedRoomIds(prev =>
            prev.includes(id) ? prev.filter(rid => rid !== id) : [...prev, id]
        )
    }

    const selectAll = () => {
        setSelectedRoomIds(rooms.map(r => r.id))
    }

    const deselectAll = () => {
        setSelectedRoomIds([])
    }

    const filteredRooms = rooms.filter(r =>
        r.code.toLowerCase().includes(roomSearch.toLowerCase())
    ).sort((a, b) => {
        if (sortBy === 'activity') return new Date(b.last_activity) - new Date(a.last_activity)
        if (sortBy === 'songs') return (b.song_count || 0) - (a.song_count || 0)
        return 0
    })

    const generateWrapped = async () => {
        if (selectedRoomIds.length === 0) {
            toast.error("Please select at least one room")
            return
        }

        setIsGenerating(true)
        try {
            const response = await fetch(`/api/analytics/wrapped?room_ids=${selectedRoomIds.join(',')}`, {
                credentials: 'include'
            })
            if (!response.ok) throw new Error("Failed to fetch wrapped data")
            const data = await response.json()
            setWrappedData(data)
            setCurrentSlide(0)
        } catch (error) {
            console.error(error)
            toast.error("Error generating wrapped results")
        } finally {
            setIsGenerating(false)
        }
    }

    const exportToCSV = async () => {
        if (selectedRoomIds.length === 0) {
            toast.error("Please select at least one room")
            return
        }

        try {
            window.location.href = `/api/analytics/export?room_ids=${selectedRoomIds.join(',')}`
            toast.success("Preparing CSV download...")
        } catch (error) {
            console.error(error)
            toast.error("Error exporting CSV")
        }
    }

    const getSlides = (data) => {
        if (!data) return []
        return [
            {
                id: 'intro',
                title: "Your Year in Karaoke",
                value: "Karaoke Wrapped",
                label: `Based on ${selectedRoomIds.length} rooms`,
                icon: <Sparkles className="w-16 h-16 text-primary-400 animate-pulse" />,
                bg: "from-indigo-600 via-purple-600 to-pink-500"
            },
            {
                id: 'total_time',
                title: "The Marathon",
                value: `${data.total_minutes}`,
                label: "Minutes of pure vocal energy",
                icon: <Clock className="w-16 h-16 text-yellow-400" />,
                bg: "from-orange-500 to-red-600"
            },
            {
                id: 'total_songs',
                title: "The Headliner",
                value: `${data.total_songs}`,
                label: "Tracks performed with passion",
                icon: <Music className="w-16 h-16 text-cyan-400" />,
                bg: "from-blue-600 to-indigo-700"
            },
            {
                id: 'top_songs',
                title: "The Greatest Hits",
                list: data.top_songs,
                label: "Your most requested anthems",
                icon: <Trophy className="w-16 h-16 text-yellow-500" />,
                bg: "from-purple-700 to-indigo-900"
            },
            {
                id: 'top_artists',
                title: "The Absolute Icons",
                list: data.top_artists,
                label: "The artists you loved the most",
                icon: <Users className="w-16 h-16 text-pink-400" />,
                bg: "from-pink-600 to-rose-700"
            },
            {
                id: 'peak_time',
                title: "Peak Energy",
                value: `${data.peak_hours.indexOf(Math.max(...data.peak_hours))}:00`,
                label: "When the party was at its loudest",
                icon: <Calendar className="w-16 h-16 text-emerald-400" />,
                bg: "from-emerald-600 to-teal-800"
            },
            {
                id: 'summary',
                title: "Your Lyric Recap",
                isSummary: true,
                label: "The whole story in one glance",
                icon: <Sparkles className="w-16 h-16 text-primary-400" />,
                bg: "from-indigo-900 via-slate-900 to-black"
            }
        ]
    }

    const slides = getSlides(wrappedData)

    const nextSlide = () => {
        if (currentSlide < slides.length - 1) setCurrentSlide(prev => prev + 1)
    }

    const prevSlide = () => {
        if (currentSlide > 0) setCurrentSlide(prev => prev - 1)
    }

    // Keyboard support
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!wrappedData) return
            if (e.key === 'ArrowRight') nextSlide()
            if (e.key === 'ArrowLeft') prevSlide()
            if (e.key === 'Escape') resetWrapped()
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [wrappedData, currentSlide, slides.length])

    const resetWrapped = () => {
        setWrappedData(null)
        setSelectedRoomIds([])
    }

    if (wrappedData) {
        const currentSlideData = slides[currentSlide]

        return (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-0 md:p-6 overflow-hidden">
                <AnimatedGradientBackground className="absolute inset-0 z-0" brightness={80} />

                <div className="relative z-10 w-full max-w-lg h-full max-h-[900px] flex flex-col bg-black/40 backdrop-blur-3xl md:rounded-[40px] shadow-2xl overflow-hidden border border-white/10 animate-in fade-in zoom-in duration-500">
                    {/* Header/Progress */}
                    <div className="p-6 flex gap-1 justify-center pt-10">
                        {slides.map((_, i) => (
                            <div key={i} className={cn(
                                "h-1 rounded-full transition-all duration-500",
                                i === currentSlide ? "flex-[2] bg-white" : i < currentSlide ? "flex-1 bg-white/40" : "flex-1 bg-white/10"
                            )} />
                        ))}
                    </div>

                    {/* Content Area */}
                    <div className="flex-1 flex flex-col items-center justify-center px-10 text-center relative group/slides">
                        {/* Click Zones for easier navigation */}
                        <div
                            className="absolute inset-y-0 left-0 w-1/4 z-20 cursor-pointer"
                            onClick={(e) => { e.stopPropagation(); prevSlide(); }}
                        />
                        <div
                            className="absolute inset-y-0 right-0 w-3/4 z-20 cursor-pointer"
                            onClick={(e) => { e.stopPropagation(); nextSlide(); }}
                        />

                        <div className="mb-12 animate-in slide-in-from-bottom-8 duration-700 delay-100">
                            {currentSlideData.icon}
                        </div>

                        <h2 className="text-xl font-black uppercase tracking-[0.2em] text-white/60 mb-4 animate-in fade-in duration-700 delay-200">
                            {currentSlideData.title}
                        </h2>

                        {currentSlideData.value && (
                            <div className="relative">
                                <span className="text-8xl md:text-9xl font-black text-white tracking-tighter animate-in zoom-in-75 duration-700 delay-300">
                                    {currentSlideData.value}
                                </span>
                            </div>
                        )}

                        {currentSlideData.list && (
                            <div className="w-full space-y-4 animate-in slide-in-from-bottom-12 duration-700 delay-300">
                                {currentSlideData.list.map((item, i) => (
                                    <div key={i} className="flex items-center justify-between p-4 rounded-2xl bg-white/5 border border-white/10 group hover:bg-white/10 transition-all">
                                        <div className="flex items-center gap-4 text-left">
                                            <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-xs font-black text-white">
                                                {i + 1}
                                            </div>
                                            <div>
                                                <div className="text-white font-bold leading-tight">{item.name}</div>
                                                {item.subtext && <div className="text-white/40 text-xs font-medium">{item.subtext}</div>}
                                            </div>
                                        </div>
                                        <div className="text-primary-400 font-black text-sm">{item.count} Plays</div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {currentSlideData.isSummary && (
                            <div className="w-full grid grid-cols-2 gap-3 animate-in fade-in zoom-in duration-1000 delay-300">
                                <div className="col-span-2 p-5 rounded-3xl bg-white/5 border border-white/10 flex flex-col items-center justify-center space-y-1">
                                    <div className="text-white/40 text-[10px] font-black uppercase tracking-widest">Total Energy</div>
                                    <div className="text-4xl font-black text-white italic">{wrappedData.total_minutes}m</div>
                                    <div className="text-white/40 text-xs font-bold">{wrappedData.total_songs} Songs Played</div>
                                </div>

                                <div className="p-4 rounded-3xl bg-white/5 border border-white/10 flex flex-col items-start space-y-1">
                                    <div className="text-primary-500 text-[10px] font-black uppercase tracking-widest">Top Song</div>
                                    <div className="text-sm font-black text-white truncate w-full">{wrappedData.top_songs[0]?.name || "N/A"}</div>
                                    <div className="text-white/40 text-[10px] truncate w-full">{wrappedData.top_songs[0]?.subtext || ""}</div>
                                </div>

                                <div className="p-4 rounded-3xl bg-white/5 border border-white/10 flex flex-col items-start space-y-1">
                                    <div className="text-purple-500 text-[10px] font-black uppercase tracking-widest">Top Artist</div>
                                    <div className="text-sm font-black text-white truncate w-full">{wrappedData.top_artists[0]?.name || "N/A"}</div>
                                    <div className="text-white/40 text-[10px]">Iconic Status</div>
                                </div>

                                <div className="col-span-2 p-4 rounded-3xl bg-white/5 border border-white/10 flex items-center justify-between">
                                    <div className="flex flex-col items-start space-y-1">
                                        <div className="text-emerald-500 text-[10px] font-black uppercase tracking-widest">Peak Time</div>
                                        <div className="text-lg font-black text-white italic">{wrappedData.peak_hours.indexOf(Math.max(...wrappedData.peak_hours))}:00</div>
                                    </div>
                                    <div className="flex -space-x-3">
                                        {[1, 2, 3].map(i => (
                                            <div key={i} className="w-8 h-8 rounded-full border-2 border-black bg-white/10 flex items-center justify-center text-[10px] font-black text-white/40">
                                                {i}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="col-span-2 mt-4 flex items-center justify-center gap-2">
                                    <div className="h-[1px] flex-1 bg-white/10" />
                                    <div className="text-[10px] font-black text-white/20 uppercase tracking-[0.3em]">Karaoke Wrapped 2025</div>
                                    <div className="h-[1px] flex-1 bg-white/10" />
                                </div>
                            </div>
                        )}

                        <p className="mt-8 text-lg font-bold text-white/80 max-w-[280px] leading-relaxed animate-in fade-in duration-700 delay-500">
                            {currentSlideData.label}
                        </p>
                    </div>

                    {/* Navigation Container */}
                    <div className="p-8 flex items-center justify-between pb-12">
                        <Button
                            variant="glass"
                            size="icon"
                            className="rounded-full h-14 w-14 border-white/10 bg-white/5 hover:bg-white/10 text-white disabled:opacity-0"
                            onClick={prevSlide}
                            disabled={currentSlide === 0}
                        >
                            <ChevronLeft className="w-6 h-6" />
                        </Button>

                        <Button
                            variant="glass"
                            className="bg-white/10 border-white/20 hover:bg-white/20 text-white font-black uppercase tracking-widest px-8 h-14 rounded-2xl"
                            onClick={currentSlide === slides.length - 1 ? resetWrapped : nextSlide}
                        >
                            {currentSlide === slides.length - 1 ? "Finish" : "Next Slide"}
                        </Button>

                        <Button
                            variant="glass"
                            size="icon"
                            className="rounded-full h-14 w-14 border-white/10 bg-white/5 hover:bg-white/10 text-white disabled:opacity-0"
                            onClick={nextSlide}
                            disabled={currentSlide === slides.length - 1}
                        >
                            <ChevronRight className="w-6 h-6" />
                        </Button>
                    </div>

                    {/* Background Accents */}
                    <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary-500/20 rounded-full blur-[120px] pointer-events-none" />
                    <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-purple-500/20 rounded-full blur-[120px] pointer-events-none" />
                </div>
            </div>
        )
    }

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                <div className="space-y-2">
                    <h2 className="text-4xl font-black text-white tracking-tighter uppercase italic">
                        Karaoke <span className="text-primary-500">Wrapped</span>
                    </h2>
                    <p className="text-slate-400 font-medium max-w-lg">
                        Select one or more rooms to generate a beautiful, Spotify-style recap of the past year's singing highlights.
                    </p>
                </div>

                <div className="flex items-center gap-3">
                    <Button variant="outline" className="border-white/10 text-slate-300 hover:text-white" onClick={deselectAll}>
                        Clear Selection
                    </Button>
                    <Button variant="glass" className="bg-white/5 border-white/10 text-slate-300 hover:text-white hover:bg-white/10" onClick={exportToCSV} disabled={selectedRoomIds.length === 0}>
                        <Download className="w-4 h-4 mr-2" /> Export to CSV
                    </Button>
                    <Button variant="glass" className="bg-primary-500/10 border-primary-500/30 text-primary-400 hover:bg-primary-500/20" onClick={selectAll}>
                        Select All Rooms
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Room Selection Sidebar */}
                <Card className="lg:col-span-1 border-white/10 bg-white/5 backdrop-blur-md overflow-hidden flex flex-col h-[600px]">
                    <div className="p-4 border-b border-white/10 space-y-4 bg-white/5">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                            <Input
                                placeholder="Search by room code..."
                                value={roomSearch}
                                onChange={(e) => setRoomSearch(e.target.value)}
                                className="pl-10 bg-black/20 border-white/10 text-white placeholder:text-slate-600 focus:ring-primary-500/50"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 mr-1">Sort:</span>
                            <Button
                                variant={sortBy === 'songs' ? 'glass' : 'ghost'}
                                size="sm"
                                onClick={() => setSortBy('songs')}
                                className={cn(
                                    "text-[10px] uppercase font-bold tracking-tighter h-8 px-3 transition-all",
                                    sortBy === 'songs' ? "bg-primary-500/20 text-primary-400 border border-primary-500/30" : "text-slate-400 hover:text-white"
                                )}
                            >
                                <Music className="w-3 h-3 mr-1.5" /> Song Count
                            </Button>
                            <Button
                                variant={sortBy === 'activity' ? 'glass' : 'ghost'}
                                size="sm"
                                onClick={() => setSortBy('activity')}
                                className={cn(
                                    "text-[10px] uppercase font-bold tracking-tighter h-8 px-3 transition-all",
                                    sortBy === 'activity' ? "bg-primary-500/20 text-primary-400 border border-primary-500/30" : "text-slate-400 hover:text-white"
                                )}
                            >
                                <Clock className="w-3 h-3 mr-1.5" /> Activity
                            </Button>
                        </div>
                        <div className="flex items-center justify-between text-xs font-black uppercase tracking-widest text-slate-500 px-1">
                            <span>{selectedRoomIds.length} SELECTED</span>
                            <span>{filteredRooms.length} ROOMS</span>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-3 space-y-2 scrollbar-none">
                        {loadingRooms ? (
                            Array(5).fill(0).map((_, i) => (
                                <div key={i} className="h-16 bg-white/5 rounded-xl animate-pulse" />
                            ))
                        ) : filteredRooms.length === 0 ? (
                            <div className="h-full flex flex-col items-center justify-center text-slate-600 space-y-2 py-20">
                                <Search className="w-8 h-8 opacity-20" />
                                <p className="font-bold">No rooms found</p>
                            </div>
                        ) : (
                            filteredRooms.map(room => (
                                <button
                                    key={room.id}
                                    onClick={() => toggleRoom(room.id)}
                                    className={cn(
                                        "w-full flex items-center justify-between p-4 rounded-2xl transition-all duration-300 border group",
                                        selectedRoomIds.includes(room.id)
                                            ? "bg-primary-500/10 border-primary-500/40 shadow-lg shadow-primary-500/5"
                                            : "bg-white/5 border-white/5 hover:bg-white/10 hover:border-white/10"
                                    )}
                                >
                                    <div className="flex items-center gap-4">
                                        <div className={cn(
                                            "w-10 h-10 rounded-xl flex items-center justify-center font-black text-sm transition-all",
                                            selectedRoomIds.includes(room.id) ? "bg-primary-500 text-black" : "bg-white/10 text-white group-hover:bg-white/20"
                                        )}>
                                            {room.code.substring(0, 2)}
                                        </div>
                                        <div className="text-left">
                                            <div className="text-white font-black tracking-tight">{room.code}</div>
                                            <div className="text-slate-500 text-xs font-bold">{room.song_count || 0} songs</div>
                                        </div>
                                    </div>
                                    {selectedRoomIds.includes(room.id) && (
                                        <CheckCircle2 className="w-5 h-5 text-primary-500" />
                                    )}
                                </button>
                            ))
                        )}
                    </div>
                </Card>

                {/* Preview/Action Area */}
                <div className="lg:col-span-2 space-y-8">
                    <Card className="border-white/10 bg-white/5 backdrop-blur-md p-8 relative overflow-hidden h-[400px] flex flex-col items-center justify-center text-center">
                        <div className="relative z-10 space-y-8 max-w-md">
                            <div className="w-24 h-24 bg-white/5 rounded-[32px] border border-white/10 flex items-center justify-center mx-auto mb-8 animate-bounce-slow">
                                <Sparkles className="w-12 h-12 text-primary-400" />
                            </div>

                            <div className="space-y-3">
                                <h3 className="text-3xl font-black text-white tracking-tight uppercase italic underline decoration-primary-500 decoration-8 underline-offset-8">
                                    Ready for your recap?
                                </h3>
                                <p className="text-slate-400 text-lg font-medium leading-relaxed">
                                    We'll analyze every request, every high note, and every late-night anthem to create a cinematic review of your karaoke sessions.
                                </p>
                            </div>

                            <Button
                                className="w-full h-16 bg-white text-black hover:bg-white/90 font-black text-xl uppercase tracking-tighter rounded-2xl shadow-xl shadow-white/10 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:bg-slate-800 disabled:text-slate-500"
                                onClick={generateWrapped}
                                disabled={selectedRoomIds.length === 0 || isGenerating}
                            >
                                {isGenerating ? (
                                    <>
                                        <Clock className="w-6 h-6 animate-spin mr-3" />
                                        Analyzing Vocals...
                                    </>
                                ) : (
                                    <>
                                        <PlayCircle className="w-6 h-6 mr-3" />
                                        Generate Recap
                                    </>
                                )}
                            </Button>
                        </div>

                        {/* Fake "Slide Preview" Background Decoration */}
                        <div className="absolute inset-0 opacity-20 pointer-events-none">
                            <div className="absolute top-10 right-10 w-48 h-64 bg-indigo-500/20 rounded-3xl border border-white/20 rotate-12 flex items-center justify-center">
                                <Music className="w-12 h-12 text-indigo-400" />
                            </div>
                            <div className="absolute bottom-10 left-10 w-48 h-64 bg-pink-500/20 rounded-3xl border border-white/20 -rotate-12 flex items-center justify-center">
                                <Users className="w-12 h-12 text-pink-400" />
                            </div>
                        </div>
                    </Card>

                    {/* Feature Tips */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-6 rounded-[32px] bg-white/5 border border-white/10 flex items-start gap-4">
                            <div className="p-3 bg-blue-500/20 rounded-2xl"><LayoutPanelLeft className="w-6 h-6 text-blue-400" /></div>
                            <div>
                                <h4 className="text-white font-bold mb-1">Multi-Room Analysis</h4>
                                <p className="text-slate-500 text-sm leading-relaxed">Select multiple rooms to get a broader view of your entire venue history.</p>
                            </div>
                        </div>
                        <div className="p-6 rounded-[32px] bg-white/5 border border-white/10 flex items-start gap-4">
                            <div className="p-3 bg-purple-500/20 rounded-2xl"><Users className="w-6 h-6 text-purple-400" /></div>
                            <div>
                                <h4 className="text-white font-bold mb-1">Coming Soon: Guest Stats</h4>
                                <p className="text-slate-500 text-sm leading-relaxed">Future recaps will include "Top Singers" once guest data accumulation begins.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default AdminWrappedTab
